import { z } from 'zod';
import type { AlarmAction, AlarmEventDto } from './alarms.ts';

/** Live sensor values pushed to browsers over the WebSocket at `/api/v1/live`. */

export const LIVE_STATUSES = ['normal', 'warning', 'danger', 'offline'] as const;
export type LiveStatus = (typeof LIVE_STATUSES)[number];

export interface LiveValue {
  indicatorId: string;
  value: number | null;
  /** ISO timestamp of the measurement. */
  ts: string;
  status: LiveStatus;
}

export const liveClientMessageSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('subscribe'), tenantId: z.uuid(), indicatorIds: z.array(z.uuid()).max(5000) }),
  z.object({ type: z.literal('unsubscribe'), tenantId: z.uuid(), indicatorIds: z.array(z.uuid()).max(5000) }),
  z.object({ type: z.literal('ping') }),
]);
export type LiveClientMessage = z.infer<typeof liveClientMessageSchema>;

export type LiveServerMessage =
  | { type: 'snapshot'; tenantId: string; values: LiveValue[] }
  | { type: 'update'; tenantId: string; values: LiveValue[] }
  | { type: 'pong' }
  | { type: 'error'; code: string; message: string }
  /** alarm event opened / closed / acknowledged in a tenant the client subscribed to (any `subscribe` joins the
   *  tenant's alarm feed; `unsubscribe` with an empty `indicatorIds` leaves it) */
  | { type: 'alarm'; tenantId: string; action: AlarmAction; event: AlarmEventDto }
  /** first message of every connection: server clock (for client offset) and the staleness threshold */
  | { type: 'hello'; serverTime: string; staleAfterSec: number }
  /** a constructor document of a joined tenant was saved — viewers refetch instead of polling */
  | DocumentUpdatedMessage;

export interface DocumentUpdatedMessage {
  type: 'documentUpdated';
  tenantId: string;
  /** `sections` = the section list (create / update / reorder / delete) */
  ownerType: 'dashboard' | 'folder' | 'scene' | 'sections';
  ownerId: string;
  version: number | null;
}
